// Dashboard data hooks — polling hooks for all dashboard API endpoints.

import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

// ── Shared types ─────────────────────────────────────────────────────────────

interface HookState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
}

/**
 * Generic polling hook factory.
 * Fetches the given URL every intervalMs and returns {data, isLoading, error, lastUpdatedAt, refresh}.
 */
export function usePolling<T>(url: string | null, intervalMs = 60_000) {
  const [state, setState] = useState<HookState<T>>({
    data: null,
    isLoading: true,
    error: null,
    lastUpdatedAt: null,
  });

  const fetchData = useCallback(
    async (signal?: AbortSignal) => {
      if (!url) return;
      try {
        const res = await apiFetch(url, { credentials: "same-origin", signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as T;
        setState({
          data: json,
          isLoading: false,
          error: null,
          lastUpdatedAt: new Date(),
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
        }));
      }
    },
    [url],
  );

  useEffect(() => {
    if (!url) {
      setState({
        data: null,
        isLoading: false,
        error: null,
        lastUpdatedAt: null,
      });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true }));
    const controller = new AbortController();
    fetchData(controller.signal);
    const timer = setInterval(() => fetchData(controller.signal), intervalMs);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [url, fetchData, intervalMs]);

  const refresh = useCallback(() => fetchData(), [fetchData]);

  return { ...state, refresh };
}

// ── API response shapes ──────────────────────────────────────────────────────

/** Mirrors AggregatesFreshness in src/worker/dashboard-api.ts. */
export interface AggregatesFreshness {
  /** MAX(updated_at) across the returned keys, as the worker holds it. */
  updated_at: string | null;
  age_hours: number | null;
  slo_hours: number;
  /** True when the aggregate layer has not been rewritten within 2x its SLO. */
  is_stale: boolean;
}

export interface AggregatesResponse {
  aggregates: Record<string, unknown>;
  updated_at: string | null;
  /**
   * Optional on the wire only because a response cached before this shipped
   * won't carry it; the worker always sends it. Absent is treated as STALE by
   * `aggregatesTrendState` — an unknown freshness is never green.
   */
  freshness?: AggregatesFreshness;
  /** Optional — present only when the request was made with `?include_prior=1`. */
  prior?: Record<string, unknown>;
  prior_snapshot_day?: string | null;
}

export interface PipelineStageRow {
  pipeline_stage: string | null;
  count: number;
}

export interface PipelineResponse {
  pipeline: PipelineStageRow[];
  stages: number;
}

/**
 * A row from `/api/dashboard/members` (dash_members, PII-projected).
 *
 * Fields below are LIVE columns, in the worker's toSnakeCase() form, verified
 * against remote D1 on 2026-08-27: dash_members has 62 columns, `lib/pii.ts`
 * drops 12, and 50 reach the client. **The status column is `status_new`.**
 * (`status_access_database` also ships and is a legacy second opinion — no page
 * should read it; `status_new` is the one Airtable maintains.)
 *
 * This interface previously also declared `id?: string` and `status?: string`.
 * NEITHER column exists in dash_members — the table has no bare `id` at all
 * (it is keyed by `airtable_id`), and the status column has always been
 * `Status_New`. Declaring them optional made `m.status` typecheck as
 * `string | undefined` while being permanently `undefined` at runtime, which is
 * how MemberEngagement's `activeMembers` filter matched 0 of 151 active members
 * and rendered seven figures — including the expired-physical alert list — over
 * an empty set.
 *
 * There is deliberately NO `[key: string]: unknown` index signature. Removing
 * the two phantom declarations was necessary but NOT sufficient: an index
 * signature types `m.status` as `unknown`, and `unknown` is accepted by every
 * `asString(v: unknown)`-style reader in this codebase, so the dead filter went
 * on compiling with the declarations gone. The closed shape is what turns a
 * misspelled or invented column into TS2339 at the call site. That is the whole
 * mechanism — keep it closed.
 *
 * DO NOT re-add a field dash_members does not have. If you need a column that
 * is not listed here, confirm it against `pragma_table_info('dash_members')`
 * first, and confirm `lib/pii.ts` does not drop it. Genuinely dynamic access
 * still has an escape hatch: cast to `Record<string, unknown>` explicitly, so
 * the unchecked read is visible in the diff.
 */
export interface MemberRow {
  // ── Consumed by pages: typed precisely ────────────────────────────────────
  /** Airtable record id — the actual key. There is no bare `id` column. */
  airtable_id?: string | null;
  full_name?: string | null;
  /** THE status column. `Status_New` in D1. Not `status`. */
  status_new?: string | null;
  joined_date?: string | null;
  separation_date?: string | null;
  /**
   * The "Name (CO_ID)" composite. This — not `full_name` — is what
   * `dash_losap.Name` holds, so it is the LOSAP join key.
   */
  full_name_co_id?: string | null;

  // ── The remaining live columns, declared so the shape is closed ───────────
  // Typed `unknown` on purpose: nothing reads them today, and `unknown` forces
  // a deliberate narrowing at any future call site. Their presence here is what
  // lets the index signature go away.
  synced_at?: unknown;
  airtable_created_time?: unknown;
  co_id?: unknown;
  los_active_months?: unknown;
  gender?: unknown;
  years_since_joined?: unknown;
  status_access_database?: unknown;
  first_name?: unknown;
  last_name?: unknown;
  t_2024_co_meeting_attendance?: unknown;
  t_2023_co_meeting_attendance?: unknown;
  archive_comments_from_access?: unknown;
  days_since_joined?: unknown;
  los_active_years?: unknown;
  months_since_joined?: unknown;
  active?: unknown;
  preferred_name?: unknown;
  gear_inventory?: unknown;
  uniforms_2?: unknown;
  gear_inventory_2?: unknown;
  life_member?: unknown;
  deceased?: unknown;
  fit_test_advantage?: unknown;
  media_submission?: unknown;
  headshot?: unknown;
  driver_training_applicant?: unknown;
  physicals?: unknown;
  middle_name?: unknown;
  co_meeting_attendance?: unknown;
  g1_attendance?: unknown;
  bod_attendance?: unknown;
  roster?: unknown;
  loa_tracker?: unknown;
  fit_test_g1?: unknown;
  inactive_months?: unknown;
  membership_changes?: unknown;
  membership_changes_2?: unknown;
  pub_ed?: unknown;
  suffix?: unknown;
  recruit_training?: unknown;
  last_modified_time?: unknown;
  station_tours?: unknown;
  ems_internship_tracker?: unknown;
  accident_investigations?: unknown;
}

/**
 * `/api/dashboard/members` — rows plus the row-window completeness envelope.
 *
 * `count` is the number of rows in THIS response — it always was, but for a
 * long time it was the ONLY number on the wire, and dash_members holds 1,895
 * rows against call sites that asked for 1,000: 895 members (47%) were missing
 * with nothing to compare `count` against. `total` is the row count over the
 * SAME filter with no page cap, so under `?status=X` it is the number of
 * members with that status rather than the size of a page.
 *
 * The envelope fields are optional so a cached or older payload still
 * typechecks; `rowWindowState()` in `lib/rowWindow.ts` resolves their ABSENCE
 * to "unknown", never to "complete". Read the envelope through that helper —
 * do not branch on `truncated` by hand.
 */
export interface MembersResponse {
  members: MemberRow[];
  count: number;
  /** Rows matching the filter with no page cap; null when it could not be read. */
  total?: number | null;
  truncated?: boolean;
  completeness?: "complete" | "truncated" | "unknown";
  limit?: number;
  offset?: number;
}

/**
 * ── /api/dashboard/typeform — the OBSERVED wire, and nothing else ────────────
 *
 * `TypeformResponse { responses: TypeformRow[]; count: number }` used to live
 * here and RecruitmentFunnel read both fields off it. Neither has ever existed.
 * `/api/dashboard/typeform` is not a D1 read — it is a live proxy in front of
 * the Typeform API (`handleGetTypeform`, worker/dashboard-api.ts), and it has
 * exactly four return paths. Driving the REAL handler (2026-08-27):
 *
 *   no TYPEFORM_API_TOKEN   -> error, totalCompletions, recentResponses
 *   upstream non-2xx        -> error, totalCompletions, recentResponses
 *   upstream throws         -> error, totalCompletions, recentResponses
 *   success                 -> totalCompletions, recentResponses, insights,
 *                              fetchedAt
 *
 * `count` is absent from all four, so `typeformData.count.toLocaleString()`
 * threw `TypeError: Cannot read properties of undefined` on every render of
 * /recruitment-funnel — and the `typeformData ?` guard in front of it passed,
 * because the object itself is perfectly truthy. `responses` is absent too,
 * but it was optional-chained, so it degraded silently into an always-empty
 * 12-month trend chart instead of a crash.
 *
 * THE LESSON, and the reason this comment is this long. `TypeformResponse` had
 * NO index signature. It was already a closed type and it was still wrong,
 * because it was written from an assumption about what the endpoint "should"
 * return rather than from what it does. Closing a shape is necessary and not
 * sufficient: only an OBSERVED shape is safe. Every field below was read off
 * the handler's own construction of the payload, and the wire test
 * (RecruitmentFunnel.wire.test.ts) re-derives them from the real handler on
 * every run rather than pinning a list somebody typed.
 *
 * No index signature on any of these. If a page needs a field that is not
 * here, that is a compile error on purpose — go look at the handler.
 */

/**
 * One row of the Typeform Insights per-platform summary, passed through from
 * `GET /insights/{form}/summary` verbatim by the handler.
 *
 * Every field is optional because this is an EXTERNAL payload the worker does
 * not validate — it reduces over the array for its own totals and hands the
 * raw items on. Read them through a runtime narrowing helper, never by cast.
 */
export interface TypeformPlatformStat {
  platform?: string;
  total_visits?: number;
  unique_visits?: number;
  responses_count?: number;
  completion_rate?: number;
  average_time?: number;
}

/**
 * Present only when the Insights fetch succeeded. The handler swallows an
 * Insights failure and leaves `insights` undefined, so `undefined` here means
 * "could not be read", NOT "zero views".
 */
export interface TypeformLiveInsights {
  views?: number;
  starts?: number;
  completions?: number;
  completionRate?: number;
  platforms?: TypeformPlatformStat[];
}

export interface TypeformLiveResponse {
  /**
   * Typeform's `total_items` for the form. NULL on every error path — and null
   * is "we could not ask", never 0. Anything that renders this must make that
   * distinction visible.
   */
  totalCompletions: number | null;
  /**
   * The 25 MOST RECENT responses, hard-capped by the handler's `page_size=25`.
   * This is not a history and cannot be aggregated into one. Items are raw
   * Typeform response objects (`response_id`, `submitted_at`, `answers`,
   * `metadata` …) — `unknown` on purpose, so reading one forces a narrowing.
   * Note `metadata.platform`, not a top-level `platform`.
   */
  recentResponses: unknown[];
  insights?: TypeformLiveInsights;
  /** Only on the success path. Absent means the payload is an error payload. */
  fetchedAt?: string;
  /** Set on all three failure paths. Its presence means: do not trust figures. */
  error?: string;
}

// Live Cloudflare Analytics response shape
export interface CloudflareDailyRow {
  date: string;
  requests: number;
  pageViews: number;
  uniques: number;
}

export interface CloudflareTotals {
  totalUniques: number;
  totalPageViews: number;
  totalRequests: number;
}

export interface CloudflareLiveResponse {
  daily: CloudflareDailyRow[];
  totals: CloudflareTotals | null;
  since?: string;
  fetchedAt?: string;
  error?: string;
}

// ── useAggregates ─────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/dashboard/aggregates and polls every 60 s.
 * Returns a flat record of key → parsed value.
 *
 * Pass includePrior=true to also fetch a ~28-day-ago snapshot for trend
 * deltas on Dashboard KPI tiles.
 */
export function useAggregates(intervalMs = 60_000, includePrior = false) {
  const url = includePrior
    ? "/api/dashboard/aggregates?include_prior=1"
    : "/api/dashboard/aggregates";
  return usePolling<AggregatesResponse>(url, intervalMs);
}

// ── usePipeline ───────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/dashboard/pipeline and polls every 60 s.
 * Returns stage counts from dash_applicants.
 */
export function usePipeline(intervalMs = 60_000) {
  return usePolling<PipelineResponse>("/api/dashboard/pipeline", intervalMs);
}

// ── useMembers ────────────────────────────────────────────────────────────────

interface MembersParams {
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * The page limit every /api/dashboard/members call site passes.
 *
 * 5,000 is the API's hard maximum (`parseIntParam` in
 * `src/worker/dashboard-api.ts` clamps there). Live: 1,895 rows in dash_members
 * (remote D1, verified 2026-08-27) — 2.6x headroom. The API's own default is
 * 500 and is a fallback for ad-hoc callers, not a correctness decision for a
 * page: MemberEngagement and RiskMonitor rode an explicit 1,000 and rendered
 * 53% of the membership as though it were all of it. Every call site passes
 * this constant so the number cannot drift apart across pages; if dash_members
 * grows past 5,000 the endpoint reports truncated:true and the pages keep
 * rendering TruncationNotice — the cap is not the place to hide growth.
 */
export const MEMBERS_PAGE_LIMIT = 5000;

/**
 * Fetches GET /api/dashboard/members with optional filters and polls every 60 s.
 *
 * Pass an explicit `limit` — `MEMBERS_PAGE_LIMIT` unless you have a reason.
 */
export function useMembers(params?: MembersParams, intervalMs = 60_000) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const qsStr = qs.toString();
  const url = `/api/dashboard/members${qsStr ? `?${qsStr}` : ""}`;

  return usePolling<MembersResponse>(url, intervalMs);
}

// ── useTypeform — REMOVED ─────────────────────────────────────────────────────
//
// There were two hooks on the one endpoint. `useTypeform` typed it as the
// phantom `TypeformResponse` and sent `?since=&limit=&offset=` — parameters
// `handleGetTypeform` does not read and has never read, so the page believed
// it was asking for 2,000 responses over 12 months and was being handed the
// most recent 25 regardless. `useTypeformLive` below types the same endpoint
// correctly and is what /recruitment-funnel now uses, so there is one hook,
// one type, and no query string implying a filter that does not exist.

// ── useApplicants ─────────────────────────────────────────────────────────────

/**
 * One row of GET /api/dashboard/applicants, as the endpoint actually emits it.
 *
 * CLOSED ON PURPOSE - there is no index signature. The previous shape had one,
 * and it is what let /recruitment-funnel filter on `r.created_at ?? r.
 * submitted_at`: two fields that have never existed on this endpoint, on a type
 * that happily compiled both. Every non-"all" date range therefore matched zero
 * rows and every applicant chart on that page rendered empty over a healthy
 * 3,243-row feed. An index signature turns a typo, a stale field name, and a
 * server-side redaction into the same silent `undefined`.
 *
 * WHERE THIS LIST COMES FROM. Not from the table. `handleGetApplicants` builds
 * its projection with `nonPiiProjection()`, which introspects the live columns
 * and DROPS the PII ones, then normalizes every surviving name to snake_case.
 * The keys below were read off the JSON that handler emits when driven over the
 * live `dash_applicants` schema - see RecruitmentFunnel.applicants.test.ts,
 * which re-derives the same set from the real handler on every run rather than
 * trusting this declaration.
 *
 * Every column is TEXT in D1, so every value is `string | null`. Fields are
 * optional because a NULL column can be absent from a normalized row and
 * because the projection is computed per-request from the live schema: if a
 * source column is renamed upstream, its key stops arriving, and optional +
 * closed means the read that depended on it is a type error rather than a
 * silent empty chart.
 */
export interface ApplicantRow {
  // Sync bookkeeping
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  synced_at?: string | null;

  // Identity / display
  id2?: string | null;
  applicant_name_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  country?: string | null;

  // Application
  created?: string | null;
  from_podio_created_date?: string | null;
  interest?: string | null;
  how_did_you_hear_about_us?: string | null;
  pipeline_stage?: string | null;
  application_result?: string | null;
  bod_vote?: string | null;
  days_since_applied?: string | null;
  kpi_days_since_applied_category?: string | null;
  kpi_time_in_process?: string | null;
  existing_certifications?: string | null;
  candidate_notes?: string | null;
  membership_comments?: string | null;

  // Screening / onboarding milestones
  ais_status?: string | null;
  ais_date?: string | null;
  ais_roster?: string | null;
  screening_one_check?: string | null;
  screening_one_check_completed?: string | null;
  frs_physical_date?: string | null;
  frs_physical_expires?: string | null;
  frs_physical_results_received?: string | null;
  oems_fp_date?: string | null;
  oems_fp_expires?: string | null;

  // Parent/guardian (minor applicants)
  parent_first_name?: string | null;
  parent_last_name?: string | null;

  // ── WITHHELD SERVER-SIDE ────────────────────────────────────────────────
  // These columns EXIST on dash_applicants and are stripped by
  // `nonPiiProjection`'s denylist before the SELECT is built, so they never
  // reach the client. They are declared as `undefined` rather than omitted so
  // that the existing reads in RecruitmentFunnel's Geographic Summary keep
  // compiling AND state the truth: the value cannot arrive. Anything that
  // needs applicant geography needs a server-side change (an aggregate, or an
  // explicit allowance for coarse location) - not a client-side rename.
  city?: undefined;
  postal_code?: undefined;
  // `state` is declared for the same reason but is read NOWHERE on the page -
  // it is here only so the type describes the whole withheld set rather than
  // the subset that happens to have a reader today.
  state?: undefined;
}

export interface ApplicantsResponse {
  applicants: ApplicantRow[];
  count: number;
}

interface ApplicantsParams {
  limit?: number;
  offset?: number;
}

/**
 * Fetches GET /api/dashboard/applicants with optional filters and polls every 60 s.
 */
export function useApplicants(params?: ApplicantsParams, intervalMs = 60_000) {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const qsStr = qs.toString();
  const url = `/api/dashboard/applicants${qsStr ? `?${qsStr}` : ""}`;

  return usePolling<ApplicantsResponse>(url, intervalMs);
}

// ── useTypeformLive ───────────────────────────────────────────────────────────

/**
 * Fetches live Typeform API data from GET /api/dashboard/typeform.
 * Polls every 15 minutes (worker caches for 1 hour so actual API calls are rare).
 */
export function useTypeformLive(intervalMs = 900_000) {
  return usePolling<TypeformLiveResponse>(
    "/api/dashboard/typeform",
    intervalMs,
  );
}

// ── useCloudflare ─────────────────────────────────────────────────────────────

/**
 * Fetches live Cloudflare Analytics from GET /api/dashboard/cloudflare.
 * Polls every 15 minutes (worker caches for 1 hour).
 *
 * @param days - number of days to look back (1–90, default 30)
 */
export function useCloudflare(days = 30, intervalMs = 900_000) {
  const url = `/api/dashboard/cloudflare?days=${Math.min(Math.max(days, 1), 90)}`;
  return usePolling<CloudflareLiveResponse>(url, intervalMs);
}

// ── useYoYHistory ────────────────────────────────────────────────────────────

export interface YoYSnapshotRow {
  year: number;
  total_active: number;
  total_running: number;
  joined_that_year: number;
  separated_that_year: number;
  computed_at: string;
  /** 0 = data-derived (2020+), 1 = staffing-model estimate (2010-2019) */
  is_estimated: number;
}

interface YoYHistoryResponse {
  years: YoYSnapshotRow[];
  count: number;
}

/**
 * Fetches GET /api/dashboard/yoy-history — server-computed historical membership.
 * Uses Separation_Date method for accurate year-end counts going back to 2010.
 */
export function useYoYHistory(intervalMs = 300_000) {
  return usePolling<YoYHistoryResponse>(
    "/api/dashboard/yoy-history",
    intervalMs,
  );
}
